import {
  isNull,
  isBoolean,
  isString,
  isNumber,
  isObject,
  isArray,
  isDate,
  isUndefined
} from 'lodash'

import { isObservableArray } from 'mobx'
import { formatDate } from '../utils'

import { emailRegexp, urlRegexp } from './validation-regexps'

// eslint-disable-next-line prefer-const
let Test

const MIN_YEAR = 1900
const MAX_YEAR = 3000

const getLength = (value) => {
  if (Test.string(value)) {
    return value.trim().length
  }
  if (Test.array(value)) {
    return value.length
  }
  return null
}

const TestText = {
  isRequired() {
    return TestText.required()
  },
  required() {
    return 'Das Feld darf nicht leer sein!'
  },
  null() {
    return 'Muss `null` sein!'
  },
  boolean() {
    return TestText.bool()
  },
  bool(value) {
    return `Muss wahr oder falsch sein, ist zur Zeit \`${value}\`.`
  },
  string(value) {
    return `Muss ein Text sein, ist zur Zeit \`${value}\`.`
  },
  number(value) {
    return `Muss eine Zahl sein, ist zur Zeit \`${value}\`.`
  },
  array(value) {
    return `Muss eine Liste sein, ist zur Zeit \`${value}\`.`
  },
  object(value) {
    return `Muss ein Objekt sein, ist zur Zeit \`${value}\`.`
  },
  date(value) {
    return `Muss ein gültiges Datum sein (Jahr zwischen ${MIN_YEAR} und ${MAX_YEAR}), ist zur Zeit \`${value}\`.`
  },
  minLen(value, minLen = 0) {
    const actualLen = getLength(value)
    if (Test.array(value)) {
      return `Muss mindestens ${minLen} Element(e) beinhalten, hat zur Zeit ${actualLen} Element(e)!`
    }
    return `Muss mindestens ${minLen} Zeichen lang sein, ist zur Zeit ${actualLen} Zeichen lang!`
  },
  maxLen(value, maxLen = 0) {
    const actualLen = getLength(value)
    if (Test.array(value)) {
      return `Darf maximal ${maxLen} Element(e) beinhalten, hat zur Zeit ${actualLen} Element(e)!`
    }
    return `Darf maximal ${maxLen} Zeichen lang sein, ist zur Zeit ${actualLen} Zeichen lang!`
  },
  min(value, min = 0) {
    return `Darf minimal \`${min}\` sein, ist zur Zeit \`${value}\`!`
  },
  max(value, max = 0) {
    return `Darf maximal \`${max}\` sein, ist zur Zeit \`${value}\`!`
  },
  greater(value, threshold = 0) {
    if (Test.date(threshold)) {
      return `Muss nach dem ${formatDate(threshold)} liegen. `
        + `Ist zur Zeit \`${formatDate(value)}\`!`
    }
    return `Muss größer sein als \`${threshold}\`. Ist zur Zeit \`${value}\`!`
  },
  less(value, threshold = 0) {
    if (Test.date(threshold)) {
      return `Muss vor dem ${formatDate(threshold)} liegen. `
        + `Ist zur Zeit \`${formatDate(value)}\`!`
    }
    return `Muss kleiner sein als \`${threshold}\`. Ist zur Zeit \`${value}\`!`
  },
  email(value) {
    return `Muss eine gültige E-Mail sein. Ist zur Zeit \`${value}\`!`
  },
  url(value) {
    return `Muss eine gültige URL sein. Ist zur Zeit \`${value}\`!`
  },
  regex(value, regExpStr) {
    return `Muss dem Muster \`${regExpStr}\` entsprechen. Ist zur Zeit \`${value}\`!`
  },
  is(value, expectedValue) {
    return `Muss genau \`${expectedValue}\` sein. Ist zur Zeit \`${value}\`!`
  }
}

Test = {

  isRequired(value) {
    return Test.required(value)
  },

  required(value) {
    return !isUndefined(value)
      && isNumber(value) ? true : !!value
      && (typeof value === 'string' ? !!value.trim() : true)
  },

  null(value) {
    return isNull(value)
  },

  boolean(value) {
    return Test.bool(value)
  },

  bool(value) {
    return isBoolean(value)
  },

  string(value) {
    return isString(value)
  },

  number(value) {
    return isNumber(value * 1)
  },

  array(value) {
    return isArray(value) || isObservableArray(value)
  },

  object(value) {
    return isObject(value)
  },

  date(value) {
    const isRealDate = isDate(value) || !Number.isNaN(Date.parse(value))
    if (isRealDate) {
      const date = new Date(value)
      return date.getFullYear() >= MIN_YEAR && date.getFullYear() <= MAX_YEAR
    }
    return isRealDate
  },

  minLen(value, minLen = 0) {
    minLen *= 1
    const actualLen = getLength(value)
    if (actualLen === null) {
      return false
    }
    return minLen ? actualLen >= minLen : true
  },

  maxLen(value, maxLen = 0) {
    maxLen *= 1
    const actualLen = getLength(value)
    if (actualLen === null) {
      return false
    }
    return maxLen ? actualLen <= maxLen : true
  },

  min(value, min = 0) {
    return Test.number(value)
      && (min * 1
        ? value * 1 >= min * 1
        : true)
  },

  max(value, max = 0) {
    return Test.number(value)
      && (max * 1
        ? value * 1 <= max * 1
        : true)
  },

  greater(value, threshold = 0) {
    if (Test.date(threshold)) {
      return new Date(value).getTime() > new Date(threshold).getTime()
    }
    return Test.number(value)
      && (threshold * 1
        ? value * 1 > threshold * 1
        : true)
  },

  less(value, threshold = 0) {
    if (Test.date(threshold)) {
      return new Date(value).getTime() < new Date(threshold).getTime()
    }
    return Test.number(value)
      && (threshold * 1
        ? value * 1 < threshold * 1
        : true)
  },

  email(value) {
    return emailRegexp.test(value)
  },

  url(value) {
    return (!value || !value.trim()) ? true : urlRegexp.test(value)
  },

  regex(value, regExpStr) {
    const regExp = new RegExp(regExpStr)
    return regExp.test(value)
  },

  is(value, expectedValue) {
    return value === expectedValue
  }
}

export default class Validation {

  static parseType(type) {
    const enforce = Validation.makeEnforcer(type)
    const types = Validation.makeValidator(type).split('|')
    return { enforce, types }
  }

  static ensure(type, value/*, all = {}*/) {

    const { enforce, types } = Validation.parseType(type)

    if (!Validation.isValid(type, value)) {
      if (enforce !== undefined) {
        value = Validation.enforceType(types[types.length - 1])
      }
    }

    return value

  }

  static isEnforce(type) {
    return type.indexOf('default') !== -1
  }

  static makeEnforcer(type) {
    let value
    if (Validation.isEnforce(type)) {
      value = type.match(/default:([^|$]+)/)[1]
      if (value === 'null') {
        value = null
      }
    }
    return value
  }

  static isValid(type, value, all = {}) {
    if (typeof type === 'object') {

      if (this.isArrayValidator(type)) {
        return this.isArrayValid(type, value, all)
      }

      return this.isObjectValid(type, value, all)
    }

    const { types } = Validation.parseType(type)
    return types.every((currType) => Validation.testType(currType, value, all) === true)
  }

  static isObjectValid(validatorObject, value, all = {}) {
    // if the array has length 0 it's valid
    return !Object.keys(validatorObject)
      .filter((key) => !Validation.isValid(validatorObject[key], value[key], all))
      .length
  }

  static isArrayValidator(validator) {
    return '_self' in validator || '_each' in validator
  }

  static isArrayValid(validator, value, all = {}) {
    let isValid = true
    if ('_self' in validator) {
      const { types } = Validation.parseType(validator._self)
      isValid = types.every((currType) => Validation.testType(currType, value, all) === true)
    }
    if (isValid && '_each' in validator) {
      const itemValidator = validator._each
      if (Array.isArray(value) || isObservableArray(value)) {
        isValid = value.every((val) => this.isObjectValid(itemValidator, val, all))
      }

    }
    return isValid
  }

  static validationErrors(type, value, all = {}) {
    if (typeof type === 'object') {
      if (this.isArrayValidator(type)) {
        const errors = { valid: true, errors: [] }
        /* eslint-disable max-depth */
        if ('_self' in type) {
          const selfErrors = this.validationErrors(type._self, value, all)
          if (selfErrors && selfErrors.length) {
            errors.valid = false
            errors.errors.push(...selfErrors)
          }
        }
        if ('_each' in type) {
          const itemValidator = type._each
          if (Array.isArray(value) || isObservableArray(value)) {
            value.forEach((val) => {
              const itemErrors = this.validationErrors(itemValidator, val, all)
              Object.keys(itemErrors).forEach((key) => {
                const err = itemErrors[key]
                if (err.errors && err.errors.length) {
                  errors.valid = false
                  errors.errors.push(...err.errors)
                }
              })
            })
          }
        }
        /* eslint-enable: ax-depth */

        return errors.valid ? undefined : errors
      }

      return Object.keys(type)
        .map((key) => {
          return {
            key,
            result: Validation.validationErrors(type[key], value[key], all)
          }
        })
        .reduce((memo, valObj) => {
          memo[valObj.key] = {
            valid: !valObj.result,
            errors: valObj.result
          }
          return memo
        }, {})

    }

    const { types } = Validation.parseType(type)
    const errors = types.map((currType) => {
      const result = Validation.testType(currType, value, all)
      if (result !== true) {
        return {
          type: currType,
          value,
          result
        }
      }
      return undefined
    })
      .filter((err) => err)

    if (errors && errors.length) {
      return errors
    }

    return null
  }

  static makeValidator(type) {
    return type.replace(/default[^|$]*/, '')
      .replace(/^\|+/, '')
      .replace(/\|+$/, '')
  }

  static enforceType(type) {
    switch (type) {
      case 'date':
      case 'null':
      default:
        return null
    }
  }

  static add(validationMethods = {}) {
    Object.keys(validationMethods).forEach((name) => {
      if (!(name in Test)) {
        Test[name] = validationMethods[name]
      }
    })
  }

  static addTexts(validationTexts = {}) {
    Object.keys(validationTexts).forEach((name) => {
      if (!(name in TestText)) {
        TestText[name] = validationTexts[name]
      }
    })
  }

  static testType(rawType, value, all = {}) {
    const [complexType, ...opts] = rawType.split(':')
    let types = [complexType]

    if (complexType.indexOf('Or') > -1) {
      types = complexType.split('Or')
        .map((t) => `${t[0].toLowerCase()}${t.substr(1)}`)
    }

    let messages = []

    const isValid = types.some((type) => {
      if (!(type in Test)) {
        // in case there is no test for one type, just log the
        // missing test and accept it as valid
        // console.warn(`Validation: Missing test for type ${type}. Assuming valid.`)
        return true
      }

      // true is the only valid value!
      const result = Test[type](value, ...opts, all, Validation)

      if (result !== true) {
        if (result === false && type in TestText) {
          messages = TestText[type](value, ...opts, all, Validation)
        }
        else {
          messages = result
        }
      }

      return result === true
    })

    return isValid || messages

  }

}

export class ValidationInvalidError extends Error { }



// WEBPACK FOOTER //
// ./src/shared/utils/validation.js